import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { CustomerApiService } from '@dxp/core/api';
import { IConversation, IWorkItem } from '@dxp/core/models';
import { FormatDurationPipe, RemoveBracesPipe } from '@dxp/core/pipes';
import { SipService, VoiceService } from '@dxp/core/services';
import { WORKITEMS_STORE } from '@dxp/core/stores';
import { Duration, intervalToDuration } from 'date-fns';
import {
    ContactIconComponent,
    TelXLTruncateTextComponent,
} from '@dxp/elements';
import { toObservable } from '@angular/core/rxjs-interop';
import {
    concatMap,
    interval,
    map,
    Observable,
    Subscription,
    switchMap,
    take,
    tap,
} from 'rxjs';
import {
    ContactIconUserTypes,
    ContactIconSizes,
    UnknownCustomer,
} from '@dxp/core/types';

@Component({
    selector: 'voice-notification',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormatDurationPipe,
        TelXLTruncateTextComponent,
        ContactIconComponent,
        RemoveBracesPipe,
    ],
    templateUrl: './voice-notification.component.html',
    styleUrl: './voice-notification.component.scss',
})
export class VoiceNotificationComponent implements OnInit, OnDestroy {
    readonly #customerApiService = inject(CustomerApiService);
    readonly #sipService = inject(SipService);
    readonly #router = inject(Router);
    readonly #voiceService = inject(VoiceService);
    readonly #workItemStore = inject(WORKITEMS_STORE);

    subscriptionToCountDown!: Subscription;
    contactUserOptions = ContactIconUserTypes;
    contactUserSizes = ContactIconSizes;

    workItem = input<IWorkItem>();
    timeout = input<number>(0);

    closeModal = output();
    rejectVoiceCall = output();

    elapsedTime = signal<Duration>(
        intervalToDuration({ start: 0, end: this.timeout() * 1000 }),
    );
    progress = signal<number>(100);

    agentName = computed(() =>
        this.conversation()?.callerIdName
            ? this.conversation()?.callerIdName
            : 'Unknown agent',
    );
    conversation = computed<IConversation | undefined>(() => {
        const workItem = this.workItem();
        if (!workItem) {
            return undefined;
        }
        return workItem.conversations.find(
            conversation =>
                conversation.conversationId === workItem.primaryConversationId,
        );
    });

    readonly workItems = computed<IWorkItem[]>(() => {
        return this.#workItemStore
            .workItems()
            .filter(wi => wi.workItemId === this.workItem()?.workItemId);
    });

    workItem$ = toObservable(this.workItem);
    workItems$ = toObservable(this.workItems);
    timeout$ = toObservable(this.timeout);

    /**
     * Wait for the first emission of the workItem observable stream
     * Once we have the workItem
     * Make a single request to getCustomer endpoint
     */
    customerDetails$: Observable<string> = this.workItem$.pipe(
        concatMap(workItem =>
            this.#customerApiService.getCustomer(workItem?.customerId ?? '', [
                'PhoneNumbers',
                'ExternalProfiles',
                'CustomFields',
            ]),
        ),
        map(customer =>
            customer?.name
                ? customer.name
                : this.conversation()?.isInternalCall
                  ? 'Internal'
                  : UnknownCustomer,
        ),
    );

    countdown$ = this.timeout$.pipe(
        switchMap(value =>
            interval(1000).pipe(
                tap(value => {
                    const timeoutPercentage = Math.ceil(
                        (value / this.timeout()) * 100,
                    );
                    const deductFromTimeoutPercentage =
                        timeoutPercentage > 100 || timeoutPercentage < 0
                            ? 0
                            : 100 - timeoutPercentage;

                    this.progress.set(deductFromTimeoutPercentage);

                    /**
                     * When the countdown matches the timeout value - Close the Modal
                     *
                     * @param value starting index is 0
                     * @signal timeout needs to -1 to be the same as the starting index
                     */
                    if (value === this.timeout() - 1) {
                        this.closeModal.emit();
                    }
                }),
                take(value),
            ),
        ),
    );

    ngOnInit(): void {
        this.subscriptionToCountDown = this.countdown$.subscribe(value => {
            const updated = this.timeout() - value;
            const timingDuration = intervalToDuration({
                start: 0,
                end: updated * 1000,
            });

            this.elapsedTime.set(timingDuration);
        });

        this.workItems$.subscribe(workItem => {
            if (!workItem || workItem.length === 0) {
                this.closeModal.emit();
            }
            const conversation = workItem[0].conversations.find(
                conversation =>
                    conversation.conversationId ===
                    workItem[0].primaryConversationId,
            );
            if (
                workItem[0].workItemState !== 'Preview' &&
                !conversation?.isInternalCall
            ) {
                this.closeModal.emit();
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptionToCountDown.unsubscribe();
    }

    onAcceptClick(): void {
        const workItem = this.workItem();
        if (workItem) {
            this.#sipService.answerCall();
            this.#voiceService.inCall = true;
            this.#router.navigate([
                '/comms/voice',
                this.workItem()?.workItemId,
            ]);
        }
        this.closeModal.emit();
    }

    onRejectClick(): void {
        this.rejectVoiceCall.emit();
        this.closeModal.emit();
    }
}
